/* eslint-disable import/no-unresolved */
import { Delete, Edit, UploadFile, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePricingMutations } from "../../../hooks/usePricing";
import { CategoryPricing } from "../../../types/admin";
import Price from "../../common/Price";
// eslint-disable-next-line import/no-unresolved
import { toast } from "react-hot-toast";

interface CategoryPricingTableProps {
  categories: CategoryPricing[];
  loading: boolean;
  onEdit: (category: CategoryPricing) => void;
  onDelete: (id: string) => void;
}

// ─── Column widths ────────────────────────────────────────────────────────────
const COL = {
  category: { minWidth: 160 },
  pricingType: { minWidth: 110 },
  fixedPrice: { minWidth: 110 },
  pricePerKg: { minWidth: 110 },
  pricePerSize: { minWidth: 115 },
  description: { minWidth: 180 },
  zincKeywords: { minWidth: 200 },
  basePrice: { minWidth: 110 },
  isDefault: { minWidth: 100 },
  status: { minWidth: 105 },
  created: { minWidth: 100 },
  actions: { minWidth: 90 },
} as const;

const thSx = (col: { minWidth: number }) => ({
  fontWeight: "bold",
  whiteSpace: "nowrap" as const,
  minWidth: col.minWidth,
});

// ─── Component ────────────────────────────────────────────────────────────────
const CategoryPricingTable: React.FC<CategoryPricingTableProps> = ({
  categories,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const { uploadCategoryKeywords } = usePricingMutations();

  const handleUploadClick = (id: string) => {
    setSelectedCategoryId(id);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedCategoryId) {
      try {
        await uploadCategoryKeywords.mutateAsync({ id: selectedCategoryId, file });
        toast.success(t("admin.pricing.keywordsUploaded", "Keywords uploaded successfully"));
      } catch (error) {
        toast.error(t("admin.pricing.uploadFailed", "Failed to upload keywords"));
      }
    }
    // Reset
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSelectedCategoryId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t("admin.pricing.noCategoryPricingRules")}
        </Typography>
      </Box>
    );
  }

  const getPricingTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "fixed":
        return "primary";
      case "perkg":
        return "secondary";
      case "persize":
        return "info";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString();

  /** Single-line truncated text with a tooltip that shows the full value on hover */
  const EllipsisCell = ({
    value,
    maxWidth = 160,
  }: {
    value?: string | null;
    maxWidth?: number;
  }) => (
    <Tooltip
      title={value || ""}
      placement="top"
      disableHoverListener={!value || value === "-"}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          maxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: value ? "default" : undefined,
        }}
      >
        {value || "-"}
      </Typography>
    </Tooltip>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ boxShadow: 2, overflowX: "auto", width: "100%" }}
    >
      {/* minWidth forces a scrollbar rather than column squishing */}
      <Table size="small" sx={{ minWidth: 1400 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell sx={thSx(COL.category)}>
              {t("admin.pricing.category")}
            </TableCell>
            <TableCell sx={thSx(COL.pricingType)}>
              {t("admin.pricing.pricingType")}
            </TableCell>
            <TableCell sx={thSx(COL.fixedPrice)}>
              {t("admin.pricing.fixedPrice")}
            </TableCell>
            <TableCell sx={thSx(COL.pricePerKg)}>
              {t("admin.pricing.pricePerKg")}
            </TableCell>
            <TableCell sx={thSx(COL.pricePerSize)}>
              {t("admin.pricing.pricePerSize")}
            </TableCell>
            <TableCell sx={thSx(COL.description)}>
              {t("admin.pricing.description")}
            </TableCell>
            <TableCell sx={thSx(COL.zincKeywords)}>
              {t("admin.zincKeywords", "Zinc Keywords")}
            </TableCell>
            <TableCell sx={thSx(COL.basePrice)}>
              {t("admin.basePrice", "Base Price")}
            </TableCell>
            <TableCell sx={thSx(COL.isDefault)}>
              {t("admin.isDefault", "Is Default")}
            </TableCell>
            <TableCell sx={thSx(COL.status)}>
              {t("admin.pricing.status")}
            </TableCell>
            <TableCell sx={thSx(COL.created)}>
              {t("admin.pricing.created")}
            </TableCell>
            <TableCell sx={thSx(COL.actions)}>{t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleFileChange}
          />
          {categories.map((category) => (
            <TableRow key={category.id} hover>
              {/* Category Name */}
              <TableCell sx={{ minWidth: COL.category.minWidth }}>
                <EllipsisCell value={category.categoryName} maxWidth={150} />
              </TableCell>

              {/* Pricing Type */}
              <TableCell
                sx={{
                  minWidth: COL.pricingType.minWidth,
                  whiteSpace: "nowrap",
                }}
              >
                <Chip
                  label={category.pricingType}
                  size="small"
                  color={getPricingTypeColor(category.pricingType) as any}
                  variant="outlined"
                />
              </TableCell>

              {/* Fixed Price */}
              <TableCell
                sx={{ minWidth: COL.fixedPrice.minWidth, whiteSpace: "nowrap" }}
              >
                <Typography variant="body2" color="text.secondary">
                  <Price
                    value={category.fixedPrice}
                    fallback="-"
                    disableCentsHeuristic
                  />
                </Typography>
              </TableCell>

              {/* Price Per Kg */}
              <TableCell
                sx={{ minWidth: COL.pricePerKg.minWidth, whiteSpace: "nowrap" }}
              >
                <Typography variant="body2" color="text.secondary">
                  <Price
                    value={category.pricePerKg}
                    fallback="-"
                    disableCentsHeuristic
                  />
                </Typography>
              </TableCell>

              {/* Price Per Size */}
              <TableCell
                sx={{
                  minWidth: COL.pricePerSize.minWidth,
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <Price
                    value={category.pricePerSize}
                    fallback="-"
                    disableCentsHeuristic
                  />
                </Typography>
              </TableCell>

              {/* Description */}
              <TableCell sx={{ minWidth: COL.description.minWidth }}>
                <EllipsisCell value={category.description} maxWidth={170} />
              </TableCell>

              {/* Zinc Keywords */}
              <TableCell sx={{ minWidth: COL.zincKeywords.minWidth }}>
                <EllipsisCell value={category.zincKeywords} maxWidth={190} />
              </TableCell>

              {/* Base Price */}
              <TableCell
                sx={{ minWidth: COL.basePrice.minWidth, whiteSpace: "nowrap" }}
              >
                <Typography variant="body2" color="text.secondary">
                  <Price
                    value={category.basePrice}
                    fallback="-"
                    disableCentsHeuristic
                  />
                </Typography>
              </TableCell>

              {/* Is Default */}
              <TableCell
                sx={{ minWidth: COL.isDefault.minWidth, whiteSpace: "nowrap" }}
              >
                <Chip
                  label={
                    Boolean(category.isDefault)
                      ? t("common.yes", "Yes")
                      : t("common.no", "No")
                  }
                  size="small"
                  color={Boolean(category.isDefault) ? "warning" : "default"}
                  variant="outlined"
                />
              </TableCell>

              {/* Status */}
              <TableCell
                sx={{ minWidth: COL.status.minWidth, whiteSpace: "nowrap" }}
              >
                <Chip
                  label={
                    category.isActive
                      ? t("admin.pricing.active")
                      : t("admin.pricing.inactive")
                  }
                  size="small"
                  color={category.isActive ? "success" : "default"}
                  icon={category.isActive ? <Visibility /> : <VisibilityOff />}
                />
              </TableCell>

              {/* Created At */}
              <TableCell
                sx={{ minWidth: COL.created.minWidth, whiteSpace: "nowrap" }}
              >
                <Typography variant="body2" color="text.secondary">
                  {formatDate(category.createdAt)}
                </Typography>
              </TableCell>

              {/* Actions */}
              <TableCell
                sx={{ minWidth: COL.actions.minWidth, whiteSpace: "nowrap" }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("admin.pricing.uploadKeywords", "Upload Keywords (CSV)")}>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleUploadClick(category.id)}
                      disabled={uploadCategoryKeywords.isPending && selectedCategoryId === category.id}
                    >
                      {uploadCategoryKeywords.isPending && selectedCategoryId === category.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <UploadFile />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("common.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEdit(category)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("common.delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(category.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CategoryPricingTable;
